import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  Autocomplete,
} from '@mui/material';
import RichTextEditor from '../Common/RichTextEditor';
import TagEditor from '../Common/TagEditor';
import NicknamesEditor from '../Common/NicknamesEditor';
import RelationshipEditor from '../Common/RelationshipEditor';
import AttachmentManager from '../Common/AttachmentManager';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Create/Edit Monster component with relationship management
 */
const MonsterForm = ({ 
  onSuccess, 
  existingEntry = null, 
  suggestedTags = [] 
}) => {
  const [title, setTitle] = useState(existingEntry?.title || '');
  const [nicknames, setNicknames] = useState(existingEntry?.nicknames || '');
  const [description, setDescription] = useState(existingEntry?.description || '');
  const [tags, setTags] = useState(existingEntry?.tags || []);
  const [attachments, setAttachments] = useState(existingEntry?.attachments || []);
  const [encounteredAt, setEncounteredAt] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const allEntries = await wikiService.getAllWikiEntries();
        const locationEntries = allEntries.filter(entry => entry.type === 'location');
        setLocations(locationEntries);

        // If editing, find the existing encountered_at relationship
        if (existingEntry?.relationships) {
          const encounteredRel = existingEntry.relationships.find(
            rel => rel.type === 'encountered_at'
          );
          if (encounteredRel) {
            const location = locationEntries.find(loc => loc.id === encounteredRel.entryId);
            setEncounteredAt(location || null);
          }
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };

    loadLocations();
  }, [existingEntry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Monster name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      let entryId;

      if (existingEntry) {
        await wikiService.updateWikiEntry(existingEntry.id, {
          title,
          nicknames,
          description,
          tags,
          attachments,
        });
        entryId = existingEntry.id;
      } else {
        entryId = await wikiService.createWikiEntry({
          type: 'monster',
          title,
          nicknames,
          description,
          tags,
          attachments,
          createdBy: 'user',
        });
      }

      // Handle encountered_at relationship
      if (encounteredAt) {
        // Check if relationship already exists
        const entry = await wikiService.getWikiEntry(entryId);
        const existingRel = entry.relationships?.find(
          rel => rel.type === 'encountered_at' && rel.entryId === encounteredAt.id
        );
        
        if (!existingRel) {
          await wikiService.addRelationship(entryId, encounteredAt.id, 'encountered_at');
        }
      }

      onSuccess();
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: wikiTheme.mediumGray, borderLeft: `4px solid ${wikiTheme.primary}` }}>
      <form onSubmit={handleSubmit}>
        {error && <Alert severity="error" sx={{ mb: 2, fontFamily: wikiTheme.font }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Monster Name */}
          <Box>
            <TextField
              label="Monster Name"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Ancient Red Dragon, Beholder, Goblin Warrior"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wikiTheme.secondary,
                  fontFamily: wikiTheme.font,
                  '& fieldset': {
                    borderColor: wikiTheme.lightGray,
                  },
                  '&:hover fieldset': {
                    borderColor: wikiTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: wikiTheme.primary,
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: wikiTheme.tertiary,
                  opacity: 0.7,
                },
                '& .MuiInputLabel-root': {
                  color: wikiTheme.tertiary,
                  fontFamily: wikiTheme.font,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: wikiTheme.primary,
                },
              }}
            />
          </Box>

          {/* Nicknames */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
              Nicknames
            </Typography>
            <NicknamesEditor
              nicknames={nicknames}
              onChange={setNicknames}
            />
          </Box>

          {/* Encountered At Location */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
              Encountered At (Optional)
            </Typography>
            <Autocomplete
              value={encounteredAt}
              onChange={(event, newValue) => setEncounteredAt(newValue)}
              options={locations}
              getOptionLabel={(option) => option.title || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a location where this monster was encountered..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: wikiTheme.secondary,
                      fontFamily: wikiTheme.font,
                      '& fieldset': {
                        borderColor: wikiTheme.lightGray,
                      },
                      '&:hover fieldset': {
                        borderColor: wikiTheme.primary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: wikiTheme.primary,
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: wikiTheme.tertiary,
                      opacity: 0.7,
                    },
                  }}
                />
              )}
              ListboxProps={{
                sx: {
                  backgroundColor: wikiTheme.mediumGray,
                  '& .MuiAutocomplete-option': {
                    fontFamily: wikiTheme.font,
                    color: wikiTheme.secondary,
                    '&:hover': {
                      backgroundColor: wikiTheme.lightGray,
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: wikiTheme.dark,
                    },
                  },
                },
              }}
            />
          </Box>

          {/* Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
              Tags
            </Typography>
            <TagEditor
              tags={tags}
              onChange={setTags}
              suggestedTags={suggestedTags}
            />
          </Box>

          {/* Description */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
              Description
            </Typography>
            <Box sx={{ 
              '& .quill': {
                backgroundColor: wikiTheme.dark,
              },
              '& .ql-container': { 
                minHeight: '200px',
                maxHeight: '300px',
                fontSize: '14px',
                overflow: 'auto',
              },
              '& .ql-editor': {
                minHeight: '200px',
                maxHeight: '300px',
              }
            }}>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Describe the monster (appearance, abilities, behavior, habitat, etc.)..."
              />
            </Box>
          </Box>

          {/* Relationships */}
          {existingEntry && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
                Relationships
              </Typography>
              <RelationshipEditor
                entryId={existingEntry.id}
                currentRelationships={existingEntry.relationships || []}
                onRelationshipChange={() => {}}
                entryType="monster"
              />
            </Box>
          )}

          {/* Attachments */}
          {existingEntry && (
            <Box>
              <AttachmentManager
                entryId={existingEntry.id}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
              />
            </Box>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: wikiTheme.primary,
                color: wikiTheme.dark,
                fontFamily: wikiTheme.font,
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#e0a23d',
                },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: wikiTheme.dark }} /> : (existingEntry ? 'Update Monster' : 'Create Monster')}
            </Button>
          </Box>
        </Box>
      </form>
    </Paper>
  );
};

export default MonsterForm;
